import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Spinner,
    FormFeedback,
    Row,
    Col,
    Badge
} from 'reactstrap';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { updateRegionById } from './redux/dispatcher';

const UpdateViewRegion = ({ isOpen, toggleModal, isViewOnly, data }) => {
    const dispatch = useDispatch();
    const regionListState = useSelector((state) => state.regionListReducer);

    const validationSchema = yup.object().shape({
        name: yup
            .string()
            .min(2, 'Name must be at least 2 characters')
            .max(100, 'Name must be less than 100 characters')
            .required('Please enter region name.'),
        address: yup
            .string()
            .min(2, 'Address must be at least 2 characters')
            .max(255, 'Address must be less than 255 characters')
            .required('Please enter region address.')
    });

    const formik = useFormik({
        initialValues: {
            name: data?.name || '',
            address: data?.address || ''
        },
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values) => {
            dispatch(updateRegionById(data?.id, values, toggleModal));
        }
    });

    // Extract Regional Admin Name
    const regAdmin = data?.mapping?.regionalAdmin || data?.regionalAdmin || data?.admin;
    let adminName = '-';
    if (Array.isArray(regAdmin)) {
        adminName = regAdmin.map(a => typeof a === 'object' ? `${a?.first_name || a?.name || ''} ${a?.last_name || ''}`.trim() : String(a)).filter(Boolean).join(', ');
    } else if (typeof regAdmin === 'object' && regAdmin !== null) {
        adminName = `${regAdmin?.first_name || regAdmin?.name || ''} ${regAdmin?.last_name || ''}`.trim();
    } else if (typeof regAdmin === 'string' && regAdmin) {
        adminName = regAdmin;
    } else if (data?.first_name) {
        adminName = `${data.first_name} ${data?.last_name || ''}`.trim();
    }
    if (!adminName) adminName = '-';

    // Extract Companies List
    let rawCompanies = data?.mapping?.companies || data?.companies || data?.company_names || data?.company_list || data?.companies_list || data?.company || [];
    let companyList = [];
    if (typeof rawCompanies === 'string') {
        companyList = rawCompanies.split(',').map(s => s.trim()).filter(Boolean);
    } else if (Array.isArray(rawCompanies)) {
        companyList = rawCompanies.map(c => {
            if (typeof c === 'string') return c;
            if (typeof c === 'object' && c !== null) {
                return c?.company_name || c?.name || c?.title || '';
            }
            return '';
        }).filter(Boolean);
    }

    return (
        <Modal isOpen={isOpen} toggle={toggleModal} centered size="lg">
            <ModalHeader toggle={toggleModal}>
                {isViewOnly ? 'View Region Details' : 'Edit Region'}
            </ModalHeader>
            <Form onSubmit={formik.handleSubmit}>
                <ModalBody>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label for="name">
                                    Region Name <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    name="name"
                                    id="name"
                                    placeholder="Enter region name"
                                    disabled={isViewOnly}
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={formik.touched.name && Boolean(formik.errors.name)}
                                />
                                {formik.touched.name && formik.errors.name && (
                                    <FormFeedback>{formik.errors.name}</FormFeedback>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label for="address">
                                    Address <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    name="address"
                                    id="address"
                                    placeholder="Enter region address"
                                    disabled={isViewOnly}
                                    value={formik.values.address}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={formik.touched.address && Boolean(formik.errors.address)}
                                />
                                {formik.touched.address && formik.errors.address && (
                                    <FormFeedback>{formik.errors.address}</FormFeedback>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col md="6">
                            <FormGroup>
                                <Label for="adminName">
                                    Regional Admin
                                </Label>
                                <Input
                                    type="text"
                                    id="adminName"
                                    disabled
                                    value={adminName}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label>Company List</Label>
                                {companyList.length > 0 ? (
                                    <ul style={{ paddingLeft: '20px', marginTop: '4px' }}>
                                        {companyList.map((comp, idx) => (
                                            <li key={idx} style={{ marginBottom: '4px' }}>{comp}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>-</p>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    {!isViewOnly && (
                        <Button className="btn-primary" type="submit" disabled={regionListState?.isLoading}>
                            {regionListState?.isLoading ? <Spinner size="sm" /> : 'Update'}
                        </Button>
                    )}
                    <Button color="secondary" onClick={toggleModal}>
                        Close
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default UpdateViewRegion;
